import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { ThemePreview } from '@/components/ThemePreview';
import { useAppTheme, branchToThemeKey } from '@/contexts/ThemeContext';
import { getThemeList } from '@/lib/themes';
import { useToast } from '@/hooks/use-toast';
import type { ThemeKey, ThemePreference, ThemeMode } from '@/contexts/ThemeContext';
import SEO from '@/components/SEO';

export default function Settings() {
  const { isMobile } = useSidebar();
  const { themePreference, themeMode, setThemePreference, setThemeMode, currentThemeKey, applyTheme } = useAppTheme();
  const { toast } = useToast();
  
  // Store original values for cancel functionality
  const [originalPreference] = useState(themePreference);
  const [originalMode] = useState(themeMode);
  const [originalTheme] = useState<ThemeKey>(themePreference === 'auto' ? currentThemeKey : (themePreference as ThemeKey));
  
  const [localPreference, setLocalPreference] = useState<ThemePreference>(themePreference);
  const [localMode, setLocalMode] = useState<ThemeMode>(themeMode);
  const [selectedTheme, setSelectedTheme] = useState<ThemeKey>(
    themePreference === 'auto' ? 'default' : (themePreference as ThemeKey)
  );
  
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const themeList = getThemeList();

  // Track unsaved changes
  useEffect(() => {
    const preferenceChanged = localPreference !== originalPreference;
    const modeChanged = localMode !== originalMode;
    const themeChanged = localPreference !== 'auto' && selectedTheme !== originalTheme;
    
    setHasUnsavedChanges(preferenceChanged || modeChanged || themeChanged);
  }, [localPreference, localMode, selectedTheme, originalPreference, originalMode, originalTheme]);

  // Live preview: apply theme immediately on selection
  const handleThemeSelect = (themeKey: ThemeKey) => {
    setSelectedTheme(themeKey);
    applyTheme(themeKey);
  };

  const handleModeToggle = (isDark: boolean) => {
    const newMode = isDark ? 'dark' : 'light';
    setLocalMode(newMode);
    setThemeMode(newMode);
  };

  const handlePreferenceChange = (value: ThemePreference) => {
    setLocalPreference(value);
    if (value === 'auto') {
      // Apply auto theme immediately for live preview
      const autoTheme = branchToThemeKey(null); // TODO: Get actual user branch
      applyTheme(autoTheme);
    }
  };

  const handleCancel = () => {
    // Restore original values
    setLocalPreference(originalPreference);
    setLocalMode(originalMode);
    setSelectedTheme(originalTheme);
    setThemePreference(originalPreference);
    setThemeMode(originalMode);
    applyTheme(originalPreference === 'auto' ? branchToThemeKey(null) : originalPreference);
    setHasUnsavedChanges(false);
    
    toast({
      title: 'Changes discarded',
      description: 'Your appearance preferences have been restored.',
    });
  };

  const handleSave = () => {
    const finalPreference: ThemePreference = localPreference === 'auto' ? 'auto' : selectedTheme;
    setThemePreference(finalPreference);
    setThemeMode(localMode);
    setHasUnsavedChanges(false);
    
    // Update original values
    Object.assign(originalPreference, finalPreference);
    Object.assign(originalMode, localMode);
    
    toast({
      title: 'Settings saved',
      description: 'Your appearance preferences have been updated.',
    });
  };

  return (
    <div className="flex flex-col flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 bg-background text-foreground">
      <SEO title="Settings - VeteranOS" description="Manage your preferences" />
      
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Settings</h1>
        {!isMobile && (
          <Input placeholder="Search settings..." className="w-1/3" />
        )}
        {isMobile && <SidebarTrigger />}
      </div>

      <div className="space-y-6 max-w-5xl">
        {/* Appearance Section */}
        <Card>
          <CardHeader>
            <CardTitle>Appearance</CardTitle>
            <CardDescription>
              Customize your interface theme based on your military branch
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Theme Status */}
            <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <div 
                  className="w-4 h-4 rounded-full ring-2 ring-offset-2 ring-offset-background ring-primary/20" 
                  style={{ backgroundColor: `hsl(var(--primary))` }} 
                />
                <div>
                  <p className="text-sm font-medium">
                    Active Theme: <span className="capitalize text-primary">{currentThemeKey}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Mode: {themeMode} • Preference: {themePreference}
                  </p>
                </div>
              </div>
              {hasUnsavedChanges && (
                <Badge variant="outline" className="text-yellow-600 border-yellow-600 dark:text-yellow-500 dark:border-yellow-500">
                  Unsaved Changes
                </Badge>
              )}
            </div>

            <Separator />

            {/* Dark Mode Toggle */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base font-semibold">Dark Mode</Label>
                <p className="text-sm text-muted-foreground">
                  Toggle between light and dark appearance
                </p>
              </div>
              <Switch
                checked={localMode === 'dark'}
                onCheckedChange={handleModeToggle}
              />
            </div>

            <Separator />

            {/* Theme Mode Selection */}
            <div className="space-y-4">
              <Label className="text-base font-semibold">Theme Selection</Label>
              <RadioGroup 
                value={localPreference} 
                onValueChange={handlePreferenceChange}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="auto" id="auto" />
                  <Label htmlFor="auto" className="font-normal cursor-pointer">
                    Use my branch theme (recommended)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="manual" id="manual" />
                  <Label htmlFor="manual" className="font-normal cursor-pointer">
                    Choose a specific theme
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Separator />

            {/* Theme Preview Grid */}
            {localPreference !== 'auto' && (
              <div className="space-y-4">
                <Label className="text-base font-semibold">Select Theme</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {themeList.map((theme) => (
                    <ThemePreview
                      key={theme.key}
                      themeKey={theme.key}
                      isSelected={selectedTheme === theme.key}
                      onSelect={handleThemeSelect}
                    />
                  ))}
                </div>
              </div>
            )}

            {localPreference === 'auto' && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Your theme will automatically match your branch selection. 
                  Current theme: <span className="font-semibold text-foreground capitalize">{currentThemeKey}</span>
                </p>
              </div>
            )}

            <Separator />

            {/* Save/Cancel Buttons */}
            <div className="flex justify-end gap-3">
              {hasUnsavedChanges && (
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              )}
              <Button onClick={handleSave} disabled={!hasUnsavedChanges}>
                {hasUnsavedChanges ? 'Save Changes' : 'Saved'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live Preview Section */}
        <Card>
          <CardHeader>
            <CardTitle>Live Preview</CardTitle>
            <CardDescription>See how your selected theme looks on actual UI elements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Sample buttons */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Buttons</Label>
              <div className="flex flex-wrap gap-3">
                <Button>Primary Button</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
              </div>
            </div>
            
            <Separator />
            
            {/* Sample badges */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Badges</Label>
              <div className="flex flex-wrap gap-2">
                <Badge>In Progress</Badge>
                <Badge variant="secondary">7 Skills</Badge>
                <Badge variant="outline">Completed</Badge>
                <Badge variant="branch">Branch Theme</Badge>
              </div>
            </div>
            
            <Separator />
            
            {/* Sample progress bar */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Progress Indicator</Label>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Mission Readiness</span>
                  <span className="text-primary font-medium">68%</span>
                </div>
                <Progress value={68} />
              </div>
            </div>
            
            <Separator />
            
            {/* Sample card */}
            <div>
              <Label className="text-sm font-medium mb-2 block">Cards</Label>
              <Card className="p-4">
                <h4 className="font-semibold text-primary mb-2">Sample Card Header</h4>
                <p className="text-sm text-muted-foreground">
                  This is how cards will look with your selected theme. The primary color is used for headings and accents.
                </p>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Profile Section Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle>Profile</CardTitle>
            <CardDescription>Manage your profile information</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Profile settings will be displayed here.
            </p>
          </CardContent>
        </Card>

        {/* Preferences Section Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle>Preferences</CardTitle>
            <CardDescription>Notification and privacy settings</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Additional preferences will be displayed here.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
